import {
  HARDWARE_VIDEO_CODEC_ORDER,
  hardwareVideoCodecFamily,
  videoCodecPolicy,
  type HardwareCodecFailureReason,
  type HardwareCodecQualification,
  type HardwareVideoCodec,
} from './mediaPolicy/VideoCodecPolicy';

type CodecLike = { mimeType: string; sdpFmtpLine?: string };

export type CodecPolicyResult<T> = {
  codecs: T[];
};

/**
 * Build the only active receive-preference list: HEVC, H.264, and associated repair codecs.
 *
 * Fails closed: when nothing in the input qualifies, `codecs` is empty. It never returns the
 * input unchanged as a "compatible" fallback, because that is how a software codec (or
 * VP8/VP9) becomes selectable. Callers must treat an empty result as "refuse" — the previous
 * `restricted` / `hardwareUnavailable` fields had no production reader and were removed.
 */
export function restrictToHardwareVideoCodecs<T extends CodecLike>(codecs: T[]): CodecPolicyResult<T> {
  return { codecs: videoCodecPolicy.orderCapabilities(codecs) };
}

const FLEET_HARDWARE_H264_PROFILE_PREFIXES = ['4200', '4d00', '6400', '42e0', '640c'];

export function h264ProfileHasFleetHardwareEncoder(profile: string): boolean {
  return FLEET_HARDWARE_H264_PROFILE_PREFIXES.some(prefix => profile.toLowerCase().startsWith(prefix));
}

export function h264ProfileOf(sdpFmtpLine?: string): string | undefined {
  return /profile-level-id=([0-9a-fA-F]+)/.exec(sdpFmtpLine ?? '')?.[1]?.toLowerCase();
}

export type NegotiatedCodecLike = {
  mimeType: string;
  clockRate?: number;
  sdpFmtpLine?: string;
};

/** `video/H264:42001f` style label used in decisions, logs and the overlay. */
export function describeNegotiatedCodec(codec: NegotiatedCodecLike): string {
  const profile = h264ProfileOf(codec.sdpFmtpLine);
  return codec.mimeType + (profile ? ':' + profile : '');
}

export type EncodeProfileProbe = (
  contentType: string,
) => Promise<{ supported?: boolean; powerEfficient?: boolean }>;

export type SendCodecDecision<T> = {
  codec: T | null;
  family?: HardwareVideoCodec;
  qualification?: HardwareCodecQualification;
  failureReason?: HardwareCodecFailureReason;
  probeUnavailable?: boolean;
  /**
   * The sender already carried a pin from earlier in this session and it was kept as-is.
   * `codec` is that existing pin. Nothing was probed and setParameters was not called.
   */
  alreadyPinned?: boolean;
  pinAccepted?: boolean;
  /** True when the sender has no encodings/negotiated codecs yet; the caller MUST retry. */
  pinRetryPending?: boolean;
  /**
   * True when the guest omitted its hardware-decode families. The host qualified on its own
   * hardware alone and made no claim about the guest; callers must not read the absence as a
   * guest refusal.
   */
  guestHardwareDecodeUnknown?: boolean;
  considered: string[];
  hardware: string[];
  guestSoftwareDecode: string[];
};

export interface HardwareSendCodecInput<T extends NegotiatedCodecLike> {
  negotiatedCodecs: readonly T[];
  probe: EncodeProfileProbe | null;
  guestHardwareDecodeFamilies: readonly string[] | undefined;
}

const PROBE_TIMEOUT_MS = 500;

/**
 * How long a retry-pending pin may stay unanswered before the host treats it as a
 * refusal. A sender normally has encodings within a negotiate round-trip; past this
 * window the absence of an answer is itself the failure.
 */
export const SENDER_PIN_RETRY_WINDOW_MS = 2_000;

function label(codec: NegotiatedCodecLike): string {
  return describeNegotiatedCodec(codec);
}

function contentTypeFor(codec: NegotiatedCodecLike): string {
  const family = hardwareVideoCodecFamily(codec.mimeType);
  if (family === 'hevc') {
    const profileId = /profile-id=(\d+)/.exec(codec.sdpFmtpLine ?? '')?.[1];
    return `video/H265;profile-id=${profileId ?? '1'}`;
  }
  return codec.sdpFmtpLine ? `video/H264;${codec.sdpFmtpLine}` : 'video/H264';
}

async function probeHardware(
  probe: EncodeProfileProbe,
  codec: NegotiatedCodecLike,
): Promise<boolean> {
  try {
    const pending = probe(contentTypeFor(codec));
    pending.catch(() => undefined);
    const result = await Promise.race([
      pending,
      new Promise<null>(resolve => setTimeout(() => resolve(null), PROBE_TIMEOUT_MS)),
    ]);
    return result?.supported === true && result.powerEfficient === true;
  } catch {
    return false;
  }
}

/** Select mutually hardware-qualified HEVC, then H.264, and fail closed otherwise. */
export async function chooseHardwareSendCodec<T extends NegotiatedCodecLike>(
  input: HardwareSendCodecInput<T>,
): Promise<SendCodecDecision<T>> {
  const candidates = input.negotiatedCodecs.filter(codec => hardwareVideoCodecFamily(codec.mimeType) !== null);
  const considered = candidates.map(label);
  if (!input.probe) {
    // The host cannot ask whether it has a hardware encoder. That is unknown, not a
    // negative answer, so it gets its own reason and MUST NOT be silently downgraded
    // to a software encode.
    return {
      codec: null, considered, hardware: [], guestSoftwareDecode: [], probeUnavailable: true,
      failureReason: 'host-hardware-encode-probe-unavailable',
    };
  }
  if (input.guestHardwareDecodeFamilies !== undefined && input.guestHardwareDecodeFamilies.length === 0) {
    // The guest ANSWERED: it has no hardware decoder for any family. That is a real,
    // terminal negative and the session must refuse rather than pin a software decode.
    return {
      codec: null, considered, hardware: [], guestSoftwareDecode: considered,
      failureReason: 'guest-hardware-decode-unavailable',
    };
  }

  // An ABSENT field means the guest did not tell us (a legacy/rolling-compat peer). The
  // field's contract is that absence behaves exactly as before it existed: the host picks
  // from its OWN hardware and makes no claim about the guest. Collapsing absent with an
  // explicit empty list refused a session to every peer that simply omits the field, even
  // when host H.264 hardware qualified.
  const guestHardwareKnown = input.guestHardwareDecodeFamilies !== undefined;
  const guestFamilies = new Set(
    (input.guestHardwareDecodeFamilies ?? []).map(value => hardwareVideoCodecFamily(value)).filter(Boolean),
  );
  const hostHardware = new Map<T, HardwareCodecQualification>();
  await Promise.all(candidates.map(async codec => {
    if (await probeHardware(input.probe!, codec)) hostHardware.set(codec, 'runtime-probe');
  }));

  // FLEET H.264 SAFETY NET — do not remove.
  //
  // `encodingInfo` under-reports hardware for constrained-baseline H.264 on otherwise
  // perfectly capable GPUs, and Safari's H.264 offer is exactly constrained-baseline. Without
  // this, the probe answers "not power-efficient", H.264 never qualifies, the pin fails, and
  // the strict-hardware policy refuses the session — so a Safari guest is torn down and
  // reconnects forever. Every modern PC has a hardware H.264 encoder; a profile that is on
  // the fleet-proven list is hardware-qualified even when the probe cannot see it, and once
  // ANY sibling H.264 profile probed hardware, the family's hardware encoder exists.
  const anyProbedH264 = candidates.some(codec =>
    hardwareVideoCodecFamily(codec.mimeType) === 'h264' && hostHardware.has(codec));
  for (const codec of candidates) {
    if (hardwareVideoCodecFamily(codec.mimeType) !== 'h264') continue;
    const profile = h264ProfileOf(codec.sdpFmtpLine);
    if (anyProbedH264 || (profile && h264ProfileHasFleetHardwareEncoder(profile))) {
      if (!hostHardware.has(codec)) hostHardware.set(codec, 'fleet-proven-h264-profile');
    }
  }

  const mutual = candidates.filter(codec => {
    const family = hardwareVideoCodecFamily(codec.mimeType);
    if (family === null || !hostHardware.has(codec)) return false;
    // An ABSENT guest field means "not told", not "cannot": qualify on host hardware alone.
    // An EXPLICIT list is authoritative and intersected.
    //
    // Do NOT narrow the unknown case to H.264. That looks safer but is strictly worse: it
    // excludes HEVC from `mutual` before HARDWARE_VIDEO_CODEC_ORDER is ever consulted, so a
    // host with hardware HEVC (and a Safari guest that decodes it in hardware) is forced down
    // to H.264 — and on constrained-baseline H.264 the probe under-reports hardware, which
    // lands on the refusal path and tears the session down. Absent must fall through to the
    // same host-hardware qualification it had before the field existed.
    return !guestHardwareKnown || guestFamilies.has(family);
  });
  const guestSoftware = candidates.filter(codec => {
    const family = hardwareVideoCodecFamily(codec.mimeType);
    return hostHardware.has(codec) && guestHardwareKnown && (family === null || !guestFamilies.has(family));
  });

  for (const family of HARDWARE_VIDEO_CODEC_ORDER) {
    // Prefer a codec the runtime probe actually confirmed over one the fleet table inferred.
    const codec = mutual.find(candidate =>
      hardwareVideoCodecFamily(candidate.mimeType) === family
      && hostHardware.get(candidate) === 'runtime-probe')
      ?? mutual.find(candidate => hardwareVideoCodecFamily(candidate.mimeType) === family);
    if (codec) {
      return {
        codec,
        family,
        qualification: hostHardware.get(codec),
        considered,
        hardware: mutual.map(label),
        guestSoftwareDecode: guestSoftware.map(label),
        ...(guestHardwareKnown ? {} : { guestHardwareDecodeUnknown: true }),
      };
    }
  }
  return {
    codec: null,
    considered,
    hardware: [],
    guestSoftwareDecode: guestSoftware.map(label),
    failureReason: hostHardware.size === 0
      ? 'host-hardware-encode-unavailable'
      : 'no-mutual-hardware-codec',
  };
}
